import { fetchJSON as fetchAPI } from './client';

export type HealthScoreComponent = {
  key: string; name: string; current_value?: string; historical?: string; target?: string;
  gap?: string; priority?: string; business_impact?: string; evidence?: string[];
};

export type CampaignHealthScore = {
  campaign_id: string; campaign_name?: string; overall_score: number;
  components: HealthScoreComponent[]; dna_origin: boolean; generated_at: string;
};

export type AdRecommendation = {
  id: string; title: string; category: string; business_objective: string;
  evidence?: string[]; root_cause?: string; expected_revenue?: string; expected_profit?: string;
  expected_roas?: string; expected_organic_lift?: string; expected_inventory_impact?: string;
  confidence: number; priority: string; effort: string; risk: string;
  rollback_strategy?: string; observation_window?: string; status: string;
  action_label: string; dna_evidence: boolean;
};

export type AdSimulationResult = {
  campaign_id: string; recommendation_id: string; estimated_spend?: string; estimated_sales?: string;
  estimated_orders?: string; estimated_ctr?: string; estimated_cvr?: string; estimated_roas?: string;
  estimated_acos?: string; estimated_profit?: string; organic_rank_impact?: string;
  inventory_impact?: string; cash_flow_impact?: string; confidence: number; risk: string;
  historical_similarity?: string; key_assumptions?: string[]; generated_at: string;
};

export type KeywordInsight = {
  keyword: string; match_type: string; score: number; ctr?: string; cvr?: string;
  roas?: string; profit?: string; intent?: string; opportunity?: string; confidence: number;
};

export type OpportunityInsight = {
  id: string; title: string; type: string; expected_profit?: string; priority: string; confidence: number;
};

export type AttributionResult = {
  factor: string; contribution: string; revenue_impact?: string; profit_impact?: string;
  data_type: string; confidence: number;
};

export type CampaignVersion = {
  id: string; campaign_id: string; version: number; status: string; author?: string;
  approved: boolean; created_at: string; summary?: string;
};

export type ExperimentLink = {
  id: string; campaign_id: string; name: string; status: string;
  baseline_kpi?: string; observation_window?: string; created_at: string;
};

export type CompetitorAdGap = {
  dimension: string; our_value: string; competitor_value: string; gap: string;
  data_type: string; priority: string;
};

export type AdTimelineEvent = {
  id: string; campaign_id: string; type: string; title: string; actor?: string; timestamp: string;
};

const COMMAND_BASE = import.meta.env.VITE_COMMAND_API ?? '/api/v1/command';
const fetchCmd = <T,>(path: string, init?: RequestInit) => fetchAPI<T>(`${COMMAND_BASE}${path}`, init);

export const advertisingGrowthIntelligenceApi = {
  health: (campaignId: string) =>
    fetchCmd<CampaignHealthScore>(`/advertising-growth/${encodeURIComponent(campaignId)}/health`),
  recommendations: (campaignId: string) =>
    fetchCmd<{ recommendations: AdRecommendation[] }>(`/advertising-growth/${encodeURIComponent(campaignId)}/recommendations`).then((r) => r.recommendations),
  simulate: (campaignId: string, recommendationId: string) =>
    fetchCmd<AdSimulationResult>(`/advertising-growth/${encodeURIComponent(campaignId)}/simulate`, {
      method: 'POST', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ recommendation_id: recommendationId }),
    }),
  keywords: (campaignId: string) =>
    fetchCmd<{ keywords: KeywordInsight[] }>(`/advertising-growth/${encodeURIComponent(campaignId)}/keywords`).then((r) => r.keywords),
  opportunities: (campaignId: string) =>
    fetchCmd<{ opportunities: OpportunityInsight[] }>(`/advertising-growth/${encodeURIComponent(campaignId)}/opportunities`).then((r) => r.opportunities),
  attribution: (campaignId: string) =>
    fetchCmd<{ attribution: AttributionResult[] }>(`/advertising-growth/${encodeURIComponent(campaignId)}/attribution`).then((r) => r.attribution),
  versions: (campaignId: string) =>
    fetchCmd<{ versions: CampaignVersion[] }>(`/advertising-growth/${encodeURIComponent(campaignId)}/versions`).then((r) => r.versions),
  experiments: (campaignId: string) =>
    fetchCmd<{ experiments: ExperimentLink[] }>(`/advertising-growth/${encodeURIComponent(campaignId)}/experiments`).then((r) => r.experiments),
  history: (campaignId: string) =>
    fetchCmd<{ history: AdTimelineEvent[] }>(`/advertising-growth/${encodeURIComponent(campaignId)}/history`).then((r) => r.history),
  competitor: (campaignId: string) =>
    fetchCmd<{ gaps: CompetitorAdGap[] }>(`/advertising-growth/${encodeURIComponent(campaignId)}/competitor`).then((r) => r.gaps),
};
